import { useEffect, useSyncExternalStore } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';

const KEY = 'tc-crm-theme';
// Custom DOM event the store/setters dispatch so subscribers (App,
// Settings, etc.) can react in the same tab - `storage` events only fire
// across tabs, which doesn't help us in a single-window Electron app.
const CHANGE_EVENT = 'tc-crm-theme-change';

export function getStoredTheme(): ThemePref {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(KEY);
  if (v === 'light' || v === 'dark' || v === 'system') return v;
  return 'system';
}

export function storeTheme(theme: ThemePref): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(KEY, theme);
  // Notify same-tab subscribers - useSyncExternalStore listeners hooked to
  // this event will re-read getStoredTheme() and re-render.
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * Resolve a theme preference to the boolean "should the document have the
 * .dark class". Mirrors Tailwind's `darkMode: 'class'` requirement.
 *
 * Exported so unit tests can exercise the matchMedia branch without
 * spinning up the full React hook.
 */
export function resolveDark(theme: ThemePref): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  // system - follow the OS / browser preference.
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export function applyTheme(theme: ThemePref): void {
  if (typeof document === 'undefined') return;
  const dark = resolveDark(theme);
  document.documentElement.classList.toggle('dark', dark);
  // Tell native form controls (date, select, checkbox) to pick the right
  // built-in palette so they don't render bright white on a dark surface.
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}

/**
 * Reactive hook that returns the current stored theme preference. Re-renders
 * whenever storeTheme() is called (same tab) or another tab/process writes
 * to the localStorage key. Use this where the App needs to apply or watch
 * the user's preference - captures-stale-state bugs go away.
 */
export function useStoredTheme(): ThemePref {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === 'undefined') return () => undefined;
      window.addEventListener(CHANGE_EVENT, cb);
      window.addEventListener('storage', cb);
      return () => {
        window.removeEventListener(CHANGE_EVENT, cb);
        window.removeEventListener('storage', cb);
      };
    },
    getStoredTheme,
    () => 'system',
  );
}

/**
 * Hook that subscribes to OS-level theme changes when the user has
 * preference "system". Re-applies the resolved theme whenever it flips.
 *
 * Pass the current preference (from useStoredTheme) - when it changes the
 * watcher resubscribes so it stops reacting to OS changes once the user
 * picks light or dark explicitly.
 */
export function useThemeWatcher(theme: ThemePref): void {
  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);
}
